import { useOutletContext } from 'react-router';

import { WithNonNullable } from '@synapex/tools/typescript';

import {
  AdvertiserAccount,
  Workspace,
  OrdData,
  Permission,
  Subscription,
  ActiveUser,
  Owner,
  NewAccountChannelRequest,
  ExistentAccountChannelRequest,
} from '../entity';

//basic authorized
export type AuthorizedOutletContext = {
  activeUser: ActiveUser;

  activeAccount: AdvertiserAccount | null;

  activeNewAccountChannelRequest: NewAccountChannelRequest | null;

  activeWorkspace: Workspace | null;

  activeChannelRequest: ExistentAccountChannelRequest | null;

  owner: Owner | null;

  ordData: OrdData | null;

  permission: Permission | null;

  subscription: Subscription | null;
};

export const useAuthorizedOutletContext = () => {
  return useOutletContext<AuthorizedOutletContext>();
};

//active new account channel request
export type NewAccountChannelRequestOutletContext = WithNonNullable<
  AuthorizedOutletContext,
  'activeNewAccountChannelRequest'
>;
export function isNewAccountChannelRequestOutletContext(
  v: AuthorizedOutletContext,
): v is NewAccountChannelRequestOutletContext {
  return Boolean(v.activeNewAccountChannelRequest);
}

export const useNewAccountChannelRequestContext = () => {
  return useOutletContext<NewAccountChannelRequestOutletContext>();
};

//active account
export type ActiveAccountOutletContext = WithNonNullable<
  AuthorizedOutletContext,
  'activeAccount' | 'permission' | 'subscription' | 'ordData' | 'owner'
>;
export function isActiveAccountOutletContext(
  v: AuthorizedOutletContext,
): v is ActiveAccountOutletContext {
  return Boolean(
    v.activeAccount && v.permission && v.subscription && v.ordData && v.owner,
  );
}

export const useActiveAccountContext = () => {
  return useOutletContext<ActiveAccountOutletContext>();
};

//active workspace
export type ActiveWorkspaceOutletContext = WithNonNullable<
  AuthorizedOutletContext,
  'activeWorkspace' | 'permission' | 'subscription' | 'ordData' | 'owner'
>;

export function isWorkspaceOutletContext(
  v: AuthorizedOutletContext,
): v is ActiveWorkspaceOutletContext {
  return Boolean(
    v.activeWorkspace && v.permission && v.subscription && v.ordData && v.owner,
  );
}

export const useActiveWorkspaceContext = () => {
  return useOutletContext<ActiveWorkspaceOutletContext>();
};

//active channel request
export type ChannelRequestOutletContext = WithNonNullable<
  AuthorizedOutletContext,
  | 'activeAccount'
  | 'permission'
  | 'subscription'
  | 'ordData'
  | 'owner'
  | 'activeChannelRequest'
>;

export function isChannelRequestOutletContext(
  v: AuthorizedOutletContext,
): v is ChannelRequestOutletContext {
  return Boolean(
    v.activeAccount &&
      v.permission &&
      v.subscription &&
      v.ordData &&
      v.owner &&
      v.activeChannelRequest,
  );
}

export const useChannelRequestContext = () => {
  return useOutletContext<ChannelRequestOutletContext>();
};
